import React from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';
import ProductGallery from '../components/Product/ProductGallery';
import ProductInfo from '../components/Product/ProductInfo';
import { categoryLabels, findProduct, isCategory } from '../data/products';

const Section = styled.section`
  padding: 8rem 0 6rem;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const Breadcrumb = styled.nav`
  font-family: ${({ theme }) => theme.fonts.accent};
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.lightText};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  letter-spacing: 0.05em;

  a {
    color: ${({ theme }) => theme.colors.lightText};

    &:hover {
      color: ${({ theme }) => theme.colors.text};
    }
  }

  span[aria-current] {
    color: ${({ theme }) => theme.colors.text};
  }
`;

const Layout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spacing.xxl};
  }
`;

const NotFoundHeading = styled.h1`
  font-size: 2rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Product: React.FC = () => {
  const { category, slug } = useParams<{ category: string; slug: string }>();

  if (!isCategory(category) || !slug) {
    return (
      <Section>
        <Container>
          <NotFoundHeading>Not found</NotFoundHeading>
          <Link to="/collections">Back to collections</Link>
        </Container>
      </Section>
    );
  }

  const product = findProduct(category, slug);
  if (!product) {
    return (
      <Section>
        <Container>
          <Breadcrumb aria-label="Breadcrumb">
            <Link to="/collections">Collections</Link> /{' '}
            <Link to={`/collections/${category}`}>{categoryLabels[category]}</Link>
          </Breadcrumb>
          <NotFoundHeading>Piece not found</NotFoundHeading>
          <Link to={`/collections/${category}`}>Back to {categoryLabels[category]}</Link>
        </Container>
      </Section>
    );
  }

  return (
    <Section>
      <Container>
        <Breadcrumb aria-label="Breadcrumb">
          <Link to="/collections">Collections</Link> /{' '}
          <Link to={`/collections/${category}`}>{categoryLabels[category]}</Link> /{' '}
          <span aria-current="page">{product.name}</span>
        </Breadcrumb>
        <Layout>
          <ProductGallery imageIds={product.imageIds} alt={product.name} />
          <ProductInfo product={product} />
        </Layout>
      </Container>
    </Section>
  );
};

export default Product;
